import type { AddMaterialType } from '../../../deck/materials';
import { ADD_MATERIAL_TYPES, unopenedMaterials } from '../../../deck/materials';
import { Button } from '../../components/Button';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
  DropdownTrigger,
} from '../../components/Dropdown';
import { CirclePlus, Plus } from '../../icons';
import type {
  AddMaterialDropdownItemsProps,
  AddMaterialMenuEntry,
  AddMaterialMenuLabels,
  DeckMaterialsAddMenuProps,
  DeckMaterialsPlusMenuProps,
} from './DeckMaterials.types';
import { addMaterialIcon, DECK_MATERIAL_ICONS } from './icons';

/**
 * The "+" menu's rows as data — label, icon (from the ONE material icon
 * table) and availability — so every shell renders the same six entries in
 * the same order: the dropdown below on web and tablet, a bottom sheet of
 * `MenuListItem`s on the phone.
 *
 * Two ways a type can be less than available, deliberately distinct:
 *  - `disabledTypes` — listed, greyed, with `disabledHint` under it. For a
 *    type this deck cannot take RIGHT NOW (a reader who cannot add, a limit
 *    reached): the row keeps its place and says why.
 *  - `hiddenTypes` — left out altogether. For a type this PLATFORM has no
 *    path for at all (YouTube on mobile): a row that can never be enabled
 *    here is not a promise worth making, and "available on the web app"
 *    under it is a dead end on a phone.
 */
export function addMaterialMenuEntries(
  labels: AddMaterialMenuLabels,
  disabledTypes: readonly AddMaterialType[] = [],
  disabledHint?: string,
  hiddenTypes: readonly AddMaterialType[] = [],
): AddMaterialMenuEntry[] {
  return ADD_MATERIAL_TYPES.filter((type) => !hiddenTypes.includes(type)).map((type) => {
    const disabled = disabledTypes.includes(type);
    return {
      type,
      label: labels[type],
      icon: addMaterialIcon(type),
      disabled,
      hint: disabled ? disabledHint : undefined,
    };
  });
}

/** The rows, for a `<DropdownContent>` — used by {@link DeckMaterialsAddMenu}
 *  and by any host that owns its own `Dropdown` root. */
export function AddMaterialDropdownItems({
  entries,
  onPick,
  testID,
}: AddMaterialDropdownItemsProps) {
  return (
    <>
      {entries.map((entry) => (
        <DropdownItem
          key={entry.type}
          icon={entry.icon}
          disabled={entry.disabled}
          description={entry.hint}
          onSelect={() => onPick(entry.type)}
          testID={testID ? `${testID}-${entry.type}` : undefined}>
          {entry.label}
        </DropdownItem>
      ))}
    </>
  );
}

/**
 * The BAR's "+" menu — Figma `44:1316` — beside the tabs, on web and iPad.
 *
 * Two things, in one popover: "New Source ›" FIRST — its six add-types open
 * as a flyout STACKED TO THE RIGHT — then a separator and the materials the
 * deck has that are NOT open as a tab (under "Recents", so a closed tab is
 * one click from coming back — an open one is already in the strip and would
 * only repeat it). Creating leads because it is the one row that is always
 * there: a Recents list that grows with the deck never moves it. With every
 * material open, Recents disappears and the menu is just "New Source".
 *
 * Recents is the point of THIS placement: with a material open the grid is
 * gone, so this "+" is how another existing source becomes a tab without
 * leaving the page being read. The grid's add tile opens
 * {@link DeckMaterialsAddMenu} instead — there every material is already on
 * screen, and the only thing left to do is add.
 *
 * That flyout is the DS's own `DropdownSub` — it owns the hover intent, the
 * grace delay that lets the pointer travel into the panel, the collision flip
 * when there is no room on the right, and the native counterpart (an inline
 * indented section, since a side-flyout means nothing in a bottom sheet).
 * Picking a type closes the WHOLE menu, because sub rows share the root's
 * `close()`.
 */
/** The bar's "+" panel never grows past this, however long a name is. */
const PLUS_MENU_MAX_WIDTH = 'max-w-[240px]';

export function DeckMaterialsPlusMenu({
  materials,
  state,
  addEntries,
  labels,
  onOpenMaterial,
  onPickAddType,
  accessibilityLabel,
  side = 'bottom',
  align = 'start',
  testID,
}: DeckMaterialsPlusMenuProps) {
  const recents = unopenedMaterials(materials, state);
  return (
    <Dropdown size="s" testID={testID}>
      <DropdownTrigger>
        {({ onPress }) => (
          <Button
            variant="secondary"
            size="xs"
            icon={Plus}
            accessibilityLabel={accessibilityLabel}
            onPress={onPress}
            testID={testID ? `${testID}-trigger` : undefined}
          />
        )}
      </DropdownTrigger>
      <DropdownContent
        side={side}
        align={align}
        minWidth={143}
        // Capped: a material's name is the user's own and can be a whole
        // sentence. Each row clamps it and marquees it on hover
        // (`MenuListItem`'s `MarqueeLabel`) instead of the panel stretching
        // across the page to fit it.
        className={PLUS_MENU_MAX_WIDTH}>
        <DropdownSub>
          <DropdownSubTrigger
            icon={CirclePlus}
            testID={testID ? `${testID}-new-source` : undefined}>
            {labels.newSource}
          </DropdownSubTrigger>
          <DropdownSubContent minWidth={143}>
            <AddMaterialDropdownItems
              entries={addEntries}
              onPick={onPickAddType}
              testID={testID}
            />
          </DropdownSubContent>
        </DropdownSub>
        {recents.length > 0 ? (
          <>
            <DropdownSeparator />
            <DropdownLabel>{labels.recents}</DropdownLabel>
            {recents.map((item) => (
              <DropdownItem
                key={item.key}
                icon={DECK_MATERIAL_ICONS[item.kind]}
                onSelect={() => onOpenMaterial(item.ref)}
                testID={testID ? `${testID}-open-${item.key}` : undefined}>
                {item.label}
              </DropdownItem>
            ))}
          </>
        ) : null}
      </DropdownContent>
    </Dropdown>
  );
}
(DeckMaterialsPlusMenu as { displayName?: string }).displayName = 'DeckMaterialsPlusMenu';

/**
 * The GRID's "+" menu: the six source types, and nothing else (Figma
 * `1:13847` + `1:17705`). On All Sources every material the deck holds is
 * already on screen around it, so reopening has nothing to offer and this
 * menu only adds — the bar's {@link DeckMaterialsPlusMenu} is the one that
 * also lists Recents.
 *
 * Its default trigger is the 32px icon button; `trigger` replaces it with a
 * host-drawn one that carries its own `DropdownTrigger`, which is how the
 * grid's add TILE opens this menu with the popover anchored on its "+"
 * rather than on the card (see `AddMaterialTile`). Shared shell for web and
 * tablet; the phone renders {@link addMaterialMenuEntries} in a bottom sheet
 * instead.
 */
export function DeckMaterialsAddMenu({
  labels,
  onPick,
  disabledTypes,
  disabledHint,
  accessibilityLabel,
  trigger,
  side = 'bottom',
  align = 'start',
  size = 's',
  onOpenChange,
  testID,
}: DeckMaterialsAddMenuProps) {
  const entries = addMaterialMenuEntries(labels, disabledTypes, disabledHint);
  return (
    <Dropdown size={size} onOpenChange={onOpenChange} testID={testID}>
      {trigger ?? (
        <DropdownTrigger>
          {({ onPress }) => (
            <Button
              variant="secondary"
              size="xs"
              icon={Plus}
              accessibilityLabel={accessibilityLabel}
              onPress={onPress}
              testID={testID ? `${testID}-trigger` : undefined}
            />
          )}
        </DropdownTrigger>
      )}
      <DropdownContent side={side} align={align} minWidth={160}>
        <AddMaterialDropdownItems entries={entries} onPick={onPick} testID={testID} />
      </DropdownContent>
    </Dropdown>
  );
}
(DeckMaterialsAddMenu as { displayName?: string }).displayName = 'DeckMaterialsAddMenu';
